import React, { useState, useRef, useEffect } from "react";
import {
  FaCog,
  FaBell,
  FaBars,
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaEnvelope,
  FaPlus,
  FaSearch,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import "../navbar/Navbar.css";

const Navbar = ({ collapsed, toggleMobileSidebar, isMobile, sidebarOpen }) => {
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserActions, setShowUserActions] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const notificationRef = useRef(null);
  const userActionsRef = useRef(null);
  const navigate = useNavigate();

  // Updated breadcrumb mappings
  const breadcrumbMap = {
    // Main routes
    "/": { title: "Login", parent: null },
    "/dashboard": { title: "Dashboard", parent: null },
    "/users": { title: "Users", parent: "/dashboard" },
    "/roles": { title: "Roles", parent: "/dashboard" },
    "/hr-management": { title: "HR Management", parent: "/dashboard" },
    "/doctors": { title: "Doctors", parent: "/dashboard" },
    "/fees-management": { title: "Fees Management", parent: "/dashboard" },
    "/patients": { title: "Patients", parent: "/dashboard" },
    "/patients/patient-birthday": {
      title: "Patient Birthday",
      parent: "/patients",
    },
    "/appointments": { title: "Appointments", parent: "/dashboard" },
    "/billing": { title: "Billing", parent: "/dashboard" },
    "/pharmacy": { title: "Pharmacy", parent: "/dashboard" },
    "/lab-management": { title: "Lab Management", parent: "/dashboard" },
    "/expenses": { title: "Expenses", parent: "/dashboard" },
    "/hospital-maintenance": {
      title: "Hospital Maintenance",
      parent: "/dashboard",
    },
    "/hospital-purchases": {
      title: "Hospital Purchases",
      parent: "/dashboard",
    },
    "/reports": { title: "Reports", parent: "/dashboard" },
    "/chat": { title: "Chat", parent: "/dashboard" },
    "/follow-up-calls": { title: "Follow-up Call", parent: "/dashboard" },
    "/follow-up-calls/follow-up-1": {
      title: "Follow-up 1",
      parent: "/follow-up-calls",
    },
    "/follow-up-calls/follow-up-2": {
      title: "Follow-up 2",
      parent: "/follow-up-calls",
    },
    "/follow-up-calls/courtesy-call": {
      title: "Courtesy Call",
      parent: "/follow-up-calls",
    },
    "/notifications": { title: "Notifications", parent: "/dashboard" },

    // Billing Sub-routes
    // IN BILLING TEMPORARILY DISABLED
    /*
    "/billing/in-billing": {
      title: "IN Billing",
      parent: "/billing",
    },
    "/billing/in-billing/add-billing": {
      title: "Add IN Billing",
      parent: "/billing/in-billing",
    },
    "/billing/in-billing/update-billing": {
      title: "UPDATE IN BILLING",
      parent: "/billing/in-billing",
    },
    */
    "/billing/out-billing": {
      title: "OUT Billing",
      parent: "/billing",
    },
    "/billing/out-billing/add-billing": {
      title: "Add OUT Billing",
      parent: "/billing/out-billing",
    },
    "/billing/out-billing/update-billing": {
      title: "UPDATE OUT BILLING",
      parent: "/billing/out-billing",
    },

    // Hr sub-routes
    "/hr-management/staff": {
      title: "Staff",
      parent: "/hr-management",
    },
    "/hr-management/staff/add-staff": {
      title: "Add Staff",
      parent: "/hr-management/staff",
    },
    "/hr-management/attendance": {
      title: "Attendance",
      parent: "/hr-management",
    },
    "/hr-management/attendance/attendance-calendar": {
      title: "Attendance Calendar",
      parent: "/hr-management/attendance",
    },
    "/hr-management/salary-details": {
      title: "Salary Details",
      parent: "/hr-management",
    },

    // Fee sub-routes
    "/fees-management/treatment_charges": {
      title: "Treatment Charges",
      parent: "/fees-management",
    },
    "/fees-management/doctorfee_master": {
      title: "Doctor Fee Master",
      parent: "/fees-management",
    },
    "/fees-management/dynamic_billing": {
      title: "Dynamic Billing",
      parent: "/fees-management",
    },
    "/fees-management/discount_offers": {
      title: "Discount Offers",
      parent: "/fees-management",
    },
    "/fees-management/bill_customization": {
      title: "Bill Customization",
      parent: "/fees-management",
    },

    // Pharmacy sub-routes
    "/pharmacy/medicine-master": {
      title: "Medicine Master",
      parent: "/pharmacy",
    },
    "/pharmacy/purchase-pharmacy": {
      title: "Purchase List",
      parent: "/pharmacy",
    },
    "/pharmacy/purchase-order": {
      title: "Purchase Order",
      parent: "/pharmacy",
    },
    "/pharmacy/purchase-pharmacy/add-purchase-order": {
      title: "Add Purchase Order",
      parent: "/pharmacy/purchase-order",
    },
    "/pharmacy/purchase-pharmacy/add-purchase-pharmacy": {
      title: "Add Purchase List",
      parent: "/pharmacy/purchase-pharmacy",
    },
    "/pharmacy/purchase-pharmacy/update-purchase-pharmacy/:id": {
      title: "Update Purchase List",
      parent: "/pharmacy/purchase-pharmacy",
    },
    "/pharmacy/sale-pharmacy": {
      title: "Sale Pharmacy",
      parent: "/pharmacy",
    },
    "/pharmacy/purchase-pharmacy/add-sale-pharmacy": {
      title: "Add Sale Pharmacy",
      parent: "/pharmacy/purchase-pharmacy",
    },

    // Add/edit routes
    "/roles/add": { title: "Add Roles", parent: "/roles" },
    "/roles/edit/:id": { title: "Edit Roles", parent: "/roles" },
  };

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    setIsLoadingNotifications(true);
    try {
      const response = await axios.get(`${Apiurl}/notification`, {
        params: {
          status: "pending",
        },
      });

      const notificationsData = response.data || [];
      setNotifications(notificationsData);

      // Count unread notifications
      const unreadNotifications = notificationsData.filter(notification =>
        !notification.staff_viewed && notification.status === "pending"
      ).length;

      setUnreadCount(unreadNotifications);
      console.log("Fetched notifications:", notificationsData.length);
      console.log("Unread count:", unreadNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Mark notification as viewed
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${Apiurl}/notification/${notificationId}`, {
        staff_viewed: true
      });

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif._id === notificationId
            ? { ...notif, staff_viewed: true }
            : notif
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Fetch notifications on component mount and when user role is Management
  useEffect(() => {
    const roleNames = localStorage.getItem("role_name");
    if (roleNames === "Management") {
      fetchNotifications();

      // Set up polling to refresh notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  // Global search: search navigation items plus patients/appointments.
  useEffect(() => {
    const query = globalSearch.trim().toLowerCase();

    if (!query) {
      setSearchResults([]);
      setShowGlobalSearch(false);
      return;
    }

    const navigationItems = [
      { label: "Dashboard", path: "/dashboard", keywords: "dashboard home" },
      { label: "Patients", path: "/patients", keywords: "patient patients" },
      { label: "Appointments", path: "/appointments", keywords: "appointment appointments" },
      { label: "Doctors", path: "/doctors", keywords: "doctor doctors" },
      { label: "Pharmacy", path: "/pharmacy", keywords: "pharmacy medicine medicines" },
      { label: "Follow-up Calls", path: "/follow-up-calls", keywords: "followup follow-up courtesy call" },
      { label: "Reports", path: "/reports", keywords: "report reports" },
      { label: "Lab Management", path: "/lab-management", keywords: "lab laboratory" },
    ];

    const matchedPages = navigationItems
      .filter((item) => `${item.label} ${item.keywords}`.toLowerCase().includes(query))
      .slice(0, 5)
      .map((item) => ({ ...item, kind: "page" }));

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsGlobalSearching(true);

      try {
        const [patientsResponse, appointmentsResponse] = await Promise.allSettled([
          axios.get(`${Apiurl}/patient_details`),
          axios.get(`${Apiurl}/appointments`),
        ]);

        if (cancelled) return;

        const patients =
          patientsResponse.status === "fulfilled" && Array.isArray(patientsResponse.value?.data)
            ? patientsResponse.value.data
            : [];
        const appointments =
          appointmentsResponse.status === "fulfilled" && Array.isArray(appointmentsResponse.value?.data)
            ? appointmentsResponse.value.data
            : [];

        const patientResults = patients
          .filter((patient) => {
            const values = [
              patient.patient_id,
              patient.first_name,
              patient.last_name,
              patient.mobile_no,
              patient.phone,
              patient.email,
            ];
            return values.some((value) =>
              String(value ?? "").toLowerCase().includes(query)
            );
          })
          .slice(0, 5)
          .map((patient) => ({
            kind: "patient",
            label: `${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "Patient",
            meta: `${patient.patient_id || "No ID"}${patient.mobile_no ? ` • ${patient.mobile_no}` : ""}`,
            path: `/patients/view/${patient.patient_id}`,
          }));

        const appointmentResults = appointments
          .filter((appointment) => {
            const values = [
              appointment.patient_id,
              appointment.patient_name,
              appointment.mobile_no,
              appointment.appointment_date,
            ];
            return values.some((value) =>
              String(value ?? "").toLowerCase().includes(query)
            );
          })
          .slice(0, 3)
          .map((appointment) => ({
            kind: "appointment",
            label: appointment.patient_name || appointment.patient_id || "Appointment",
            meta: appointment.appointment_date || "Appointment",
            path: "/appointments",
          }));

        setSearchResults([...matchedPages, ...patientResults, ...appointmentResults].slice(0, 10));
        setShowGlobalSearch(true);
      } finally {
        if (!cancelled) setIsGlobalSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [globalSearch]);

  const handleGlobalSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setGlobalSearch("");
      setShowGlobalSearch(false);
    }
  };

  const handleGlobalSearchSelect = (result) => {
    setGlobalSearch("");
    setShowGlobalSearch(false);
    navigate(result.path);
  };

  // Generate breadcrumb trail with proper path handling
  const generateBreadcrumbs = () => {
    const breadcrumbs = [];
    let currentPath = location.pathname;

    // First try exact match
    if (breadcrumbMap[currentPath]) {
      let path = currentPath;
      while (path && breadcrumbMap[path]) {
        breadcrumbs.unshift({
          title: breadcrumbMap[path].title,
          path: path,
        });
        path = breadcrumbMap[path].parent;
      }
      return breadcrumbs;
    }

    // If no exact match, try to match dynamic routes (like /edit/:id)
    const pathParts = currentPath.split("/");
    for (let i = pathParts.length; i > 0; i--) {
      const testPath = pathParts.slice(0, i).join("/");
      const remaining = pathParts.slice(i).join("/");

      if (breadcrumbMap[testPath]) {
        // Found a matching base path
        let path = testPath;
        while (path && breadcrumbMap[path]) {
          breadcrumbs.unshift({
            title: breadcrumbMap[path].title,
            path: path,
          });
          path = breadcrumbMap[path].parent;
        }

        // Add the dynamic part if there is any
        if (remaining) {
          breadcrumbs.push({
            title:
              remaining.split("/")[0].charAt(0).toUpperCase() +
              remaining.split("/")[0].slice(1).replace("-", " "),
            path: currentPath,
          });
        }

        return breadcrumbs;
      }
    }

    // If no match found, return basic breadcrumb
    return [
      { title: currentPath.split("/").pop() || "Dashboard", path: currentPath },
    ];
  };

  const breadcrumbs = generateBreadcrumbs();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (
        userActionsRef.current &&
        !userActionsRef.current.contains(event.target)
      ) {
        setShowUserActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    setShowUserActions(false);
  };

  const handleUserActionsClick = () => {
    setShowUserActions(!showUserActions);
    setShowNotifications(false);
  };

  const handleLogout = () => {
    console.log("User logged out");
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("role_name");
    localStorage.removeItem("role_id");
    localStorage.removeItem("user_type");
    localStorage.removeItem("department");
    localStorage.removeItem("username");
    localStorage.removeItem("branch_Name");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    navigate("/");
  };

  // Format notification time
  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24) return `${diffInHours} hr ago`;
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  // Get notification title based on action type and table name
  const getNotificationTitle = (notification) => {
    const { action_type, table_name } = notification;
    const actionText = action_type.charAt(0).toUpperCase() + action_type.slice(1);
    const tableText = table_name.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase());
    return `${actionText} ${tableText}`;
  };

  // Get notification message
  const getNotificationMessage = (notification) => {
    return `${notification.employee_name} requested to ${notification.action_type} ${notification.table_name} (ID: ${notification.table_id})`;
  };

  const [username, setUsername] = useState("Default User");
  const [userType, setUserType] = useState("Default Role");
  const [name, setname] = useState("Default Name");

  useEffect(() => {
    setUsername(localStorage.getItem("username") || "Default User");
    setUserType(localStorage.getItem("user_type") || "Default Role");
    setname(localStorage.getItem("name") || "Default Name");
  }, []);

  // Check if user is Management to show notifications
  const isManagement = localStorage.getItem("role_name") === "Management";

  return (
    <nav
      className={`navbar sticky-top bg-white border-bottom shadow-sm overflow-visible ${collapsed ? "sidebar-collapsed" : ""
        }`}
    >
      <div className="container-fluid d-flex justify-content-between align-items-center">
        {/* Left Section */}
        <div className="d-flex align-items-center">
          {isMobile && (
            <button
              className="btn btn-link p-0 me-2"
              onClick={toggleMobileSidebar}
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}

          {/* Breadcrumbs */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              {breadcrumbs.map((breadcrumb, index) => (
                <li
                  key={index}
                  className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? "breadcrumb-current" : ""
                    }`}
                >
                  {index < breadcrumbs.length - 1 ? (
                    [
                      "Pharmacy",
                      "HR Management",
                      "UPDATE OUT BILLING",
                      // "UPDATE IN BILLING", // IN BILLING TEMPORARILY DISABLED
                      "Billing",
                      "Fees Management",
                    ].includes(breadcrumb.title) ? (
                      <span className="fw-bold text-dark">
                        {breadcrumb.title}
                      </span>
                    ) : (
                      <Link
                        to={breadcrumb.path}
                        className="text-decoration-none breadcrumb-link"
                      >
                        {breadcrumb.title}
                      </Link>
                    )
                  ) : (
                    breadcrumb.title
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Right Section */}
        <div className="navbar-icons">
          <div className="navbar-global-search">
            <FaSearch className="navbar-search-icon" />
            <input
              type="search"
              value={globalSearch}
              placeholder="Search patients, appointments, pages..."
              aria-label="Global search"
              onChange={(event) => setGlobalSearch(event.target.value)}
              onFocus={() => globalSearch.trim() && setShowGlobalSearch(true)}
              onKeyDown={handleGlobalSearchKeyDown}
            />
            {showGlobalSearch && (
              <div className="global-search-dropdown">
                {isGlobalSearching ? (
                  <div className="global-search-empty">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <button
                      type="button"
                      className="global-search-result"
                      key={`${result.kind}-${result.path}-${index}`}
                      onClick={() => handleGlobalSearchSelect(result)}
                    >
                      <span className="global-search-result-title">{result.label}</span>
                      <span className="global-search-result-meta">
                        {result.meta || (result.kind === "page" ? "Page" : result.kind)}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="global-search-empty">No matching records</div>
                )}
              </div>
            )}
          </div>

          <button
  type="button"
  className="navbar-quick-action"
  onClick={() =>
    navigate("/appointments", {
      state: {
        openAddAppointment: true,
      },
    })
  }
>
  <FaPlus />
  <span>Appointment</span>
</button>
          <Link to="/patients/add-patients" className="navbar-quick-action">
            <FaPlus />
            <span>Patient</span>
          </Link>

          <FaCog className="navbar-icon" />

          {/* Show notification bell only for Management */}
          {isManagement && (
            <div className="notification-container" ref={notificationRef}>
              <div
                className="notification-bell"
                onClick={handleNotificationClick}
              >
                <FaBell className="navbar-icon" />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </div>
              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-header">
                    <h4>Notifications</h4>
                    <span className="notification-count">
                      {unreadCount} {unreadCount === 1 ? 'new' : 'new'}
                    </span>
                  </div>
                  <div className="notification-list">
                    {isLoadingNotifications ? (
                      <div className="p-3 text-center">
                        <div className="text-muted">Loading notifications...</div>
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.slice(0, 5).map((notification) => (
                        <div
                          key={notification._id}
                          className={`notification-item ${!notification.staff_viewed ? "unread" : ""
                            }`}
                          onClick={() => markAsRead(notification._id)}
                        >
                          <div className="notification-content">
                            <h5>{getNotificationTitle(notification)}</h5>
                            <p>{getNotificationMessage(notification)}</p>
                            <span className="notification-time">
                              {formatNotificationTime(notification.time)}
                            </span>
                          </div>
                          {!notification.staff_viewed && (
                            <div className="unread-dot"></div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-center">
                        <div className="text-muted">No notifications</div>
                      </div>
                    )}
                  </div>
                  <div className="notification-footer">
                    <Link to="/notifications" className="view-all-link">
                      View All Notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="user-actions-container" ref={userActionsRef}>
            <div className="navbar-user" onClick={handleUserActionsClick}>
              <div className="user-avatar-placeholder">
                {localStorage.getItem("name")?.charAt(0).toUpperCase() || "U"}
              </div>
              <span className="navbar-user-dropdown">▼</span>
            </div>
            {showUserActions && (
              <div className="user-actions-dropdown">
                <div className="user-info">
                  <div className="user-avatar-placeholder">
                    {localStorage.getItem("name")?.charAt(0).toUpperCase() ||
                      "U"}
                  </div>
                  <div className="user-details">
                    <h4>{name}</h4>
                    <p>
                      {username} / {userType}
                    </p>
                  </div>
                </div>
                <div className="user-actions-list">
                  <Link to="/profile" className="user-action-item">
                    <FaUser className="action-icon" />
                    <span>My Profile</span>
                  </Link>
                  <Link to="/messages" className="user-action-item">
                    <FaEnvelope className="action-icon" />
                    <span>Messages</span>
                  </Link>
                  <Link to="/settings" className="user-action-item">
                    <FaCog className="action-icon" />
                    <span>Settings</span>
                  </Link>
                  <div className="action-divider"></div>
                  <button
                    className="user-action-item logout-btn"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt className="action-icon" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;